import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { DefinitionList } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { date, phoneWithCountryCode } from '../../lib/format.js';

const TYPE_LABEL = {
  rera_agency: 'RERA-registered agency',
  registered_business: 'Registered business, no RERA',
  independent: 'Independent broker',
};

const REGISTRATION_LABEL = { gst: 'GST', udyam: 'Udyam', shop_establishment: 'Shop & Establishment' };
const RELATION_LABEL = { owner: 'Owner', tenant: 'Tenant', other: 'Other' };

/**
 * What an agency's verification rests on, by how it works: a RERA number,
 * another business registration, or — for an independent broker — years in
 * the trade, listings elsewhere and people to ring. A reference is ticked
 * once an admin has called them, and the tick names who did.
 */
export function VerificationDetails({ broker, canModerate }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(null);
  const verification = broker.verification || {};
  // Before types existed every agency was asked for RERA.
  const type = verification.type || 'rera_agency';

  const toggle = async (index, checked) => {
    setBusy(index);
    try {
      await api.broker.checkReference(broker._id, index, checked);
      await queryClient.invalidateQueries({ queryKey: ['admin-broker', broker._id] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack stack--sm">
      <DefinitionList
        items={[
          { label: 'Works as', value: TYPE_LABEL[type] },
          type === 'rera_agency' ? { label: 'RERA number', value: broker.registrationNumber || '—' } : {},
          type === 'registered_business'
            ? {
                label: REGISTRATION_LABEL[verification.businessRegistration?.kind] || 'Registration',
                value: verification.businessRegistration?.number || '—',
              }
            : {},
          type === 'independent' ? { label: 'Years as a broker', value: verification.yearsInBusiness ?? '—' } : {},
        ].filter((item) => item.label)}
      />

      {type === 'independent' && verification.listingLinks?.length ? (
        <div>
          <p className="muted" style={{ fontSize: 13 }}>Listings elsewhere</p>
          <ul className="stack stack--sm">
            {verification.listingLinks.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noreferrer noopener">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {type === 'independent' ? (
        <div>
          <p className="muted" style={{ fontSize: 13 }}>
            References {verification.references?.length ? '— tick each once you have called them' : ''}
          </p>
          {verification.references?.length ? (
            <ul className="stack stack--sm">
              {verification.references.map((reference, index) => (
                <li key={`${reference.phone}-${index}`}>
                  <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(reference.checkedAt)}
                      disabled={!canModerate || busy === index}
                      onChange={(event) => toggle(index, event.target.checked)}
                      aria-label={`Called ${reference.name}`}
                    />
                    <span>
                      <strong>{reference.name}</strong> ({RELATION_LABEL[reference.relation] || 'Other'}) —{' '}
                      <a href={`tel:${reference.phone}`}>{phoneWithCountryCode(reference.phone)}</a>
                      {reference.checkedAt ? (
                        <span className="muted"> · called {date(reference.checkedAt)}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ fontSize: 13 }}>None given.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default VerificationDetails;
